import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import z from "zod";
import { ParsePayload } from "zod/v4/core";

import type {
    FieldGroupAndExtendedFieldsForControlConclusionFieldKindAndControlConclusionAnswerKind,
    TestingActivity,
    TestingActivityCurrentStep,
    TestingActivityKind,
} from "api/generated/model";

import { conditionalFieldShown } from "./components/Tasks/utils";
import { ActivityField, ActivityFields } from "./types";

const MAX_STRING_LENGTH = 10000;
type ValueSchema = ReturnType<typeof getValueSchema>;

export const SAMPLES_KEY = "samples";

export function useTestingActivitySchema(resource: TestingActivity) {
    const { t } = useTranslation();
    const {
        controlConclusionFieldGroup,
        sampleConclusionFieldGroup,
        activityKind,
        currentStep,
    } = resource;

    const fields = getFieldsForStep(
        controlConclusionFieldGroup,
        activityKind,
        currentStep
    );
    const testingActivitySchema = z.object({
        fields: getTasksSchema(fields, t),
        [SAMPLES_KEY]: z.array(
            getSampleSchema(sampleConclusionFieldGroup.fields, t)
        ),
        submission: getViewKindSubmissionSchema(currentStep, t),
    });
    return testingActivitySchema;
}

const viewedSchema = z.object({
    type: z.literal("Viewed"),
    value: z.object({
        at: z.date(),
    }),
});
const notViewedSchema = z.object({
    type: z.literal("NotViewed"),
});
const viewedStatusSchema = z.discriminatedUnion("type", [
    viewedSchema,
    notViewedSchema,
]);

function getSampleSchema(sampleConclusionFields: ActivityFields, t: TFunction) {
    return z.object({
        activityId: z.guid(),
        viewed: viewedStatusSchema,
        fields: getTasksSchema(sampleConclusionFields, t),
    });
}
export type ActivitySample = z.infer<ReturnType<typeof getSampleSchema>>;

function getInvalidLiteralError(t: TFunction) {
    return {
        error: () => t("Validation_Required"),
    };
}

function firstReviewerSubmissionSchema(t: TFunction) {
    const error = getInvalidLiteralError(t);
    return z.literal(["firstReviewerAccept", "firstReviewerReject"], error);
}
export type FirstReviewerSubmissionOption = z.infer<
    ReturnType<typeof firstReviewerSubmissionSchema>
>;
function secondReviewerSubmissionSchema(t: TFunction) {
    const error = getInvalidLiteralError(t);
    return z.literal(
        [
            "secondReviewerAccept",
            "secondReviewerReject",
            "secondReviewerRejectToResponsible",
        ],
        error
    );
}
export type SecondReviewerSubmissionOption = z.infer<
    ReturnType<typeof secondReviewerSubmissionSchema>
>;

function getViewKindSubmissionSchema(
    currentStep: TestingActivityCurrentStep,
    t: TFunction
) {
    switch (currentStep) {
        case "completed":
            return z.never();
        case "firstReviewer":
            return firstReviewerSubmissionSchema(t);
        case "secondReviewer":
            return secondReviewerSubmissionSchema(t);
        case "responsible":
            return z.null();
        default:
            assertNever(currentStep);
    }
}

function getFieldsForStep(
    controlConclusionFieldGroup: FieldGroupAndExtendedFieldsForControlConclusionFieldKindAndControlConclusionAnswerKind,
    activityKind: TestingActivityKind,
    currentStep: TestingActivityCurrentStep
) {
    switch (currentStep) {
        case "responsible":
            return controlConclusionFieldGroup.fields;
        case "firstReviewer":
            switch (activityKind.kind) {
                case "WithFirstReviewer":
                case "WithSecondReviewer":
                    return activityKind.firstReviewerFieldGroup.fields;
                case "ResponsibleOnly":
                    throw new Error("Invalid state");
                default:
                    return assertNever(activityKind);
            }
        case "secondReviewer":
            switch (activityKind.kind) {
                case "WithSecondReviewer":
                    return activityKind.secondReviewerFieldGroup.fields;
                case "WithFirstReviewer":
                case "ResponsibleOnly":
                    throw new Error("Invalid state");
                default:
                    return assertNever(activityKind);
            }
        case "completed":
            return [];
        default:
            return assertNever(currentStep);
    }
}

export type TestingActivityForm = z.infer<
    ReturnType<typeof useTestingActivitySchema>
>;

function getTasksSchema(fields: ActivityFields, t: TFunction) {
    const fieldsSchema: Record<string, ValueSchema> = fields.reduce(
        (acc, field) => {
            return {
                ...acc,
                [field.id]: getValueSchema(field, t),
            };
        },
        {}
    );

    function checkFieldsRequired(ctx: ParsePayload<Record<string, unknown>>) {
        const value = ctx.value;
        const entries = Object.entries(value);
        entries.forEach(([fieldId, fieldValue]) => {
            const fieldConfig = fields.find((field) => field.id === fieldId);
            if (fieldConfig === undefined) {
                throw new Error("Expected to find matching field");
            }

            const isFieldMandatory = fieldConfig.mandatory;
            if (fieldConfig.condition) {
                const targetValue = value[fieldConfig.condition.fieldId];
                const isFieldShown = conditionalFieldShown(
                    fieldConfig.condition,
                    targetValue as string
                );
                if (isFieldMandatory && isFieldShown) {
                    validateRequiredField(fieldValue, fieldId, ctx, t);
                }
            } else if (isFieldMandatory) {
                validateRequiredField(fieldValue, fieldId, ctx, t);
            }
        });
    }
    return z.object(fieldsSchema).check(checkFieldsRequired);
}
type FieldValue = z.infer<ReturnType<typeof getValueSchema>>;
export type FieldsSchema = Record<string, FieldValue>;

function validateRequiredField(
    fieldValue: unknown,
    fieldId: string,
    ctx: ParsePayload<Record<string, unknown>>,
    t: TFunction
) {
    const hasNoValue =
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === "" ||
        (Array.isArray(fieldValue) && fieldValue.length === 0);

    if (hasNoValue) {
        ctx.issues.push({
            code: "custom",
            message: t("Validation_Required"),
            input: ctx.value,
            path: [fieldId],
        });
    }
}

function getValueSchema(field: ActivityField, t: TFunction) {
    // ! All field schemas that can be optional should be optional! All kinds of required states are handled by check (prior -> superRefine), for simplicity
    switch (field.fieldType.type) {
        case "TextInput":
        case "TextArea":
            return z.optional(
                z.string().max(MAX_STRING_LENGTH, {
                    message: t("Validation_ExceedsMax"),
                })
            );
        case "NumericInput": {
            return z.optional(z.number()).nullable();
        }
        case "RadioButtonGroup":
        case "Dropdown": {
            const { options } = field.fieldType;
            const optionsLiterals = options.map((option) => z.literal(option));
            const optionsLiteralsWithUndefined = [
                z.undefined(),
                ...optionsLiterals,
            ];
            if (options.length > 1) {
                return z
                    .union(optionsLiteralsWithUndefined, {
                        error: (issue) =>
                            issue.input === undefined
                                ? t("Validation_Required")
                                : t("Validation_Invalid"),
                    })
                    .nullable();
            } else {
                return z.optional(z.literal(options[0])).nullable();
            }
        }
        case "CheckboxGroup":
        case "FileUpload":
            return z.optional(z.array(z.string()));
        case "Effectiveness":
            return z.enum(["effective", "partiallyEffective", "notEffective"], {
                error: (issue) =>
                    issue.input === undefined
                        ? t("Validation_Required")
                        : t("Validation_Invalid"),
            });
        case "Heading": // Not an input
            return z.undefined().optional();
        default:
            assertNever(field.fieldType);
    }
}
